// src/encounter/lib/non-prescriber-plan-record.ts
// [PROD-2949] The wire shape of the non-prescriber plan (Scrips.Patient NonPrescriberPlansController)
// and the pure mapping between it and the plan views' `values` (field id → text).
//
// ONE SAVE PATH FOR TWO PLANS. The allied-health Plan step (MS-1) and the nurse's Nursing plan block
// both save through `PUT /api/Patients/{patientId}/Encounters/{encounterId}/NonPrescriberPlans/mine`.
// The server decides who may write which framework (nurse → nursing-process; allied health → the
// allied frameworks; physicians refused) and stamps the author from the token. Nothing here can carry
// an order, a prescription or a charge: the request has no such field.
//
// HOW A FIELD BECOMES AN ENTRY. Every field of the chosen framework is sent as one entry keyed by its
// field id, sorted into goals / activities / education / notes by `FIELD_KIND`. The review field goes
// to `followUp`. The server keeps an entry's id while its key keeps text, so ids are stable across
// saves. Fields of a framework the clinician is NOT on are not sent, so switching framework clears the
// old framework's entries on the server (soft-deleted there, never lost).
//
// Pure: no React, no fetch.
import {
  alliedPlanFor,
  planSections,
  type AlliedPlanFramework,
  type AlliedPlanSectionBase,
} from './allied-health-plan';
import { NURSING_PLAN_FRAMEWORK, NURSING_PLAN_SECTION, type NursingPlanFramework } from './nursing-plan';

export type PlanFrameworkKey = AlliedPlanFramework | NursingPlanFramework;
export type PlanStatus = 'draft' | 'active' | 'entered-in-error';

export interface PlanEntryDto {
  /** Response only. */
  id?: string;
  fieldKey: string;
  text: string;
}

export interface SaveNonPrescriberPlanRequest {
  frameworkKey: PlanFrameworkKey;
  status: PlanStatus;
  followUp: string | null;
  goals: PlanEntryDto[];
  activities: PlanEntryDto[];
  education: PlanEntryDto[];
  notes: PlanEntryDto[];
}

export interface NonPrescriberPlanDto {
  id: string;
  patientId: string;
  encounterId: string;
  author: { userId: string; personaId: number; role: 'nurse' | 'allied-health' };
  frameworkKey: string;
  status: PlanStatus;
  followUp: string | null;
  goals: PlanEntryDto[];
  activities: PlanEntryDto[];
  education: PlanEntryDto[];
  notes: PlanEntryDto[];
  createdDate: string;
  updatedDate: string;
  /** A provider has signed the encounter: read-only. */
  locked: boolean;
}

type EntryList = 'goals' | 'activities' | 'education' | 'notes';

/**
 * Which list a field's text belongs to. A field not named here is a narrative NOTE (assessment, PES
 * statement, evaluation, FITT components). Keys are the field ids of ./allied-health-plan.ts and
 * ./nursing-plan.ts; the tests prove every key here is a real field of some framework.
 */
export const FIELD_KIND: Readonly<Record<string, EntryList>> = {
  // goals
  'goals-primary': 'goals',
  'goals-behavioural-target': 'goals',
  'nursing-goals': 'goals',
  // activities (interventions the clinician gives or the patient carries out; never an order). The
  // FITT-VP components together describe ONE planned exercise activity, so they are read as a group by
  // their keys; precautions stay a note.
  'goals-self-monitoring': 'activities',
  'adime-intervention': 'activities',
  'adime-monitoring': 'activities',
  'fitt-frequency': 'activities',
  'fitt-intensity': 'activities',
  'fitt-time': 'activities',
  'fitt-type': 'activities',
  'fitt-volume': 'activities',
  'fitt-progression': 'activities',
  'nursing-interventions': 'activities',
  // education
  'nursing-education': 'education',
};

/** The field whose text is the plan's follow-up (review), per framework family. */
export const FOLLOW_UP_FIELDS: ReadonlySet<string> = new Set(['plan-review-interval', 'nursing-review']);

const ALLIED_FRAMEWORKS: readonly AlliedPlanFramework[] = ['goals-barriers', 'pes-adime', 'fitt-vp'];

export function isAlliedFramework(key: string | null | undefined): key is AlliedPlanFramework {
  return ALLIED_FRAMEWORKS.includes(key as AlliedPlanFramework);
}

/** The sections a framework's plan is made of (allied: spine + framework; nursing: its one section). */
export function sectionsForFramework(framework: PlanFrameworkKey): AlliedPlanSectionBase[] {
  return framework === NURSING_PLAN_FRAMEWORK ? [NURSING_PLAN_SECTION] : planSections(alliedPlanFor(framework));
}

/** The request for `framework` from the view's values. Only that framework's fields are sent. */
export function toSaveRequest(
  framework: PlanFrameworkKey,
  values: Readonly<Record<string, string>>,
  status: PlanStatus = 'draft',
): SaveNonPrescriberPlanRequest {
  const request: SaveNonPrescriberPlanRequest = {
    frameworkKey: framework,
    status,
    followUp: null,
    goals: [],
    activities: [],
    education: [],
    notes: [],
  };
  for (const section of sectionsForFramework(framework)) {
    for (const field of section.fields) {
      const text = values[field.id] ?? '';
      if (FOLLOW_UP_FIELDS.has(field.id)) {
        request.followUp = text.trim() === '' ? null : text;
        continue;
      }
      // Blank fields are sent too: a blank entry tells the server the clinician cleared it.
      request[FIELD_KIND[field.id] ?? 'notes'].push({ fieldKey: field.id, text });
    }
  }
  return request;
}

/** The view's values and framework from a stored plan. Unknown framework keys read as none. */
export function fromRecord(record: NonPrescriberPlanDto | null | undefined): {
  framework: PlanFrameworkKey | null;
  values: Record<string, string>;
} {
  if (!record) return { framework: null, values: {} };
  const framework: PlanFrameworkKey | null =
    record.frameworkKey === NURSING_PLAN_FRAMEWORK || isAlliedFramework(record.frameworkKey)
      ? (record.frameworkKey as PlanFrameworkKey)
      : null;
  const values: Record<string, string> = {};
  for (const list of [record.goals, record.activities, record.education, record.notes]) {
    for (const entry of list ?? []) values[entry.fieldKey] = entry.text;
  }
  if (record.followUp) {
    values[framework === NURSING_PLAN_FRAMEWORK ? 'nursing-review' : 'plan-review-interval'] = record.followUp;
  }
  return { framework, values };
}
